import { motion } from 'framer-motion';
import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import { createBoardMemberDraft } from '../data/boardMembers';
import { useBoardMembers } from '../hooks/useBoardMembers';
import { useSiteAuth } from '../hooks/useSiteAuth';
import { uploadBlissBoardMemberPhoto } from '../services/board-members-api';
import type { BoardMemberRecord } from '../types';

function cloneMember(member: BoardMemberRecord): BoardMemberRecord {
  return { ...member };
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }

      reject(new Error('Unable to read the selected image file.'));
    };
    reader.onerror = () => reject(new Error('Unable to read the selected image file.'));
    reader.readAsDataURL(file);
  });
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export default function PortalBoardMembers() {
  const { session } = useSiteAuth();
  const { members, storageMode, syncError, isLoading, saveMember, deleteMember } = useBoardMembers();
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [draftMember, setDraftMember] = useState<BoardMemberRecord | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  const canManageAll = session?.role === 'admin';
  const canManageBoard = canManageAll || session?.role === 'board_member';
  const isLocalFallback = storageMode === 'local';

  const ownMember = useMemo(
    () => members.find((member) => member.user_id === session?.id) ?? null,
    [members, session?.id],
  );

  useEffect(() => {
    if (!session || !canManageBoard) {
      return;
    }

    if (canManageAll) {
      if (!selectedMemberId) {
        if (!draftMember) {
          const firstMember = members[0] ?? null;
          setSelectedMemberId(firstMember?.id ?? null);
          setDraftMember(firstMember ? cloneMember(firstMember) : null);
        }
        return;
      }

      const selectedMember = members.find((member) => member.id === selectedMemberId) ?? null;
      if (selectedMember && (!draftMember || draftMember.id !== selectedMember.id)) {
        setDraftMember(cloneMember(selectedMember));
      }
      return;
    }

    if (ownMember) {
      if (selectedMemberId !== ownMember.id) {
        setSelectedMemberId(ownMember.id);
      }

      if (!draftMember || draftMember.id !== ownMember.id) {
        setDraftMember(cloneMember(ownMember));
      }
      return;
    }

    if (!draftMember || draftMember.user_id !== session.id) {
      setSelectedMemberId(null);
      setDraftMember(null);
    }
  }, [canManageAll, canManageBoard, draftMember, members, ownMember, selectedMemberId, session]);

  if (!session) {
    return null;
  }

  if (!canManageBoard) {
    return (
      <motion.div
        className="portal-page"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <section className="portal-section portal-section--feature">
          <p className="info-kicker">Board workspace</p>
          <h1>Board profile editing is limited to board members and admins.</h1>
          <p className="portal-feature-copy">
            If your access needs to change, contact Victoria&apos;s Place so the right portal role can be assigned.
          </p>
          <div className="portal-inline-actions">
            <Link to="/portal/account" className="button-secondary tap-target">
              Open account
            </Link>
            <Link to="/contact" className="button-primary tap-target">
              Contact the team
            </Link>
          </div>
        </section>
      </motion.div>
    );
  }

  function beginNewMemberDraft() {
    if (!session) {
      return;
    }

    const nextOrder =
      members.reduce((max, member) => Math.max(max, member.display_order), -1) + 1;
    const nextDraft = createBoardMemberDraft(
      canManageAll
        ? {
            display_order: nextOrder,
          }
        : {
            user_id: session.id,
            name: session.name,
            board_title: 'Board Member',
            photo_alt: `${session.name} portrait`,
            display_order: nextOrder,
          },
    );

    setSelectedMemberId(null);
    setDraftMember(nextDraft);
    setFeedback(null);
    setError(null);
  }

  function updateDraft(updates: Partial<BoardMemberRecord>) {
    setDraftMember((current) =>
      current
        ? {
            ...current,
            ...updates,
          }
        : current,
    );
    setFeedback(null);
    setError(null);
  }

  async function handlePhotoUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';

    if (!file || !draftMember) {
      return;
    }

    setIsUploadingPhoto(true);
    setFeedback(null);
    setError(null);

    try {
      const nextPhotoUrl =
        storageMode === 'bliss'
          ? (await uploadBlissBoardMemberPhoto(draftMember.id, file)).url
          : await readFileAsDataUrl(file);

      updateDraft({
        photo_url: nextPhotoUrl,
        photo_alt: draftMember.photo_alt || `${draftMember.name || 'Board member'} portrait`,
      });
      setFeedback(
        storageMode === 'bliss'
          ? 'Photo uploaded. Save the profile to publish the change.'
          : 'Photo added locally. Save the profile to keep the change in this browser.',
      );
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : 'Unable to upload the selected board member photo right now.',
      );
    } finally {
      setIsUploadingPhoto(false);
    }
  }

  async function handleSave() {
    if (!draftMember) {
      return;
    }

    if (!draftMember.name.trim()) {
      setError('Add the board member name before saving.');
      return;
    }

    if (!draftMember.board_title.trim()) {
      setError('Add the board title before saving.');
      return;
    }

    setIsSaving(true);
    setFeedback(null);
    setError(null);

    try {
      const savedMember = await saveMember({
        ...draftMember,
        name: draftMember.name.trim(),
        board_title: draftMember.board_title.trim(),
        bio: draftMember.bio.trim(),
        photo_alt: draftMember.photo_alt.trim() || `${draftMember.name.trim()} portrait`,
        updated_at: Date.now(),
      });

      setSelectedMemberId(savedMember.id);
      setDraftMember(cloneMember(savedMember));
      setFeedback(
        isLocalFallback
          ? 'Board member profile saved in local fallback mode only. This change is stored in this browser and is not published to Bliss.'
          : 'Board member profile saved to Bliss.',
      );
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : 'Unable to save the board member profile right now.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete() {
    if (!draftMember || !canManageAll) {
      return;
    }

    setIsSaving(true);
    setFeedback(null);
    setError(null);

    try {
      await deleteMember(draftMember.id);
      const remainingMembers = members.filter((member) => member.id !== draftMember.id);
      setSelectedMemberId(remainingMembers[0]?.id ?? null);
      setDraftMember(remainingMembers[0] ? cloneMember(remainingMembers[0]) : null);
      setFeedback('Board member profile removed.');
    } catch (deleteError) {
      setError(
        deleteError instanceof Error
          ? deleteError.message
          : 'Unable to remove the board member profile right now.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <motion.div
      className="portal-page"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    >
      <section className="portal-hero">
        <div className="portal-hero-copy">
          <p className="portal-kicker">Board members</p>
          <h1>Keep board bios current without sending updates through email chains.</h1>
          <p>
            Board members can maintain their own public profile here. Admins can edit the full roster,
            update titles, control visibility, upload photos, and remove profiles when needed.
          </p>
        </div>

        <div className="portal-hero-note">
          <span className="info-kicker">Storage mode</span>
          <strong>{storageMode === 'bliss' ? 'Bliss-connected' : 'Local fallback'}</strong>
          <span>
            {storageMode === 'bliss'
              ? 'Board member profiles are reading from the connected Bliss app API.'
              : 'Board member profiles are currently using local fallback storage in this browser.'}
          </span>
        </div>
      </section>

      {syncError ? (
        <div className="auth-runtime-note">
          <p>{syncError}</p>
        </div>
      ) : null}

      <div className="portal-board-grid">
        {canManageAll ? (
          <section className="portal-section">
            <div className="portal-section-heading">
              <div>
                <p className="info-kicker">Roster</p>
                <h2>Board profiles</h2>
              </div>
              <button
                type="button"
                className="button-secondary tap-target"
                onClick={beginNewMemberDraft}
              >
                Add board member
              </button>
            </div>

            {isLoading ? (
              <div className="auth-runtime-note">
                <p>Loading board profiles...</p>
              </div>
            ) : members.length ? (
              <div className="portal-board-list">
                {members.map((member) => (
                  <button
                    key={member.id}
                    type="button"
                    className={`portal-board-card ${
                      selectedMemberId === member.id ? 'portal-board-card--active' : ''
                    }`}
                    onClick={() => {
                      setSelectedMemberId(member.id);
                      setDraftMember(cloneMember(member));
                      setFeedback(null);
                      setError(null);
                    }}
                  >
                    <div>
                      <strong>{member.name}</strong>
                      <span>{member.board_title}</span>
                    </div>
                    <span>{member.is_visible ? 'Visible' : 'Hidden'}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="auth-runtime-note">
                <p>No board profiles exist yet. Create the first one here.</p>
              </div>
            )}
          </section>
        ) : null}

        <section className="portal-section">
          <div className="portal-section-heading">
            <div>
              <p className="info-kicker">
                {canManageAll ? 'Editor' : 'Your profile'}
              </p>
              <h2>
                {draftMember
                  ? `Edit ${draftMember.name || 'board profile'}`
                  : 'Create your board profile'}
              </h2>
            </div>

            {!canManageAll && !draftMember ? (
              <button
                type="button"
                className="button-primary tap-target"
                onClick={beginNewMemberDraft}
              >
                Create my board profile
              </button>
            ) : null}
          </div>

          {!draftMember ? (
            <div className="auth-runtime-note">
              <p>
                {canManageAll
                  ? 'Select a board profile from the roster or create a new one.'
                  : 'Create your profile to add a photo and bio to the public board page.'}
              </p>
            </div>
          ) : (
            <div className="portal-board-editor-grid">
              <div className="portal-board-photo-panel">
                <div className="portal-board-photo-preview">
                  {draftMember.photo_url ? (
                    <img
                      src={draftMember.photo_url}
                      alt={draftMember.photo_alt || `${draftMember.name} portrait`}
                    />
                  ) : (
                    <div className="portal-board-photo-placeholder" aria-hidden="true">
                      <span>{getInitials(draftMember.name || 'Board Member')}</span>
                    </div>
                  )}
                </div>

                <div className="portal-upload-actions">
                  <label className="button-secondary tap-target portal-upload-button">
                    <input
                      className="portal-file-input"
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                      onChange={(event) => void handlePhotoUpload(event)}
                      disabled={isUploadingPhoto || isSaving}
                    />
                    {isUploadingPhoto ? 'Uploading photo...' : 'Upload photo'}
                  </label>

                  {draftMember.photo_url ? (
                    <button
                      type="button"
                      className="button-ghost tap-target portal-danger-button"
                      onClick={() => updateDraft({ photo_url: '' })}
                      disabled={isUploadingPhoto || isSaving}
                    >
                      Remove photo
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="portal-form-grid portal-board-form-grid">
                {isLocalFallback ? (
                  <div className="auth-runtime-note portal-field-span-full">
                    <p>Local fallback mode is active.</p>
                    <p>
                      Board profile edits made here stay in this browser only until the Bliss board-member connection is
                      fixed. They will not appear on the public site for other visitors.
                    </p>
                  </div>
                ) : null}

                <label className="auth-field">
                  <span>Name</span>
                  <input
                    className="auth-input"
                    type="text"
                    value={draftMember.name}
                    onChange={(event) => updateDraft({ name: event.target.value })}
                    disabled={isSaving}
                  />
                </label>

                <label className="auth-field">
                  <span>Board title</span>
                  <input
                    className="auth-input"
                    type="text"
                    value={draftMember.board_title}
                    onChange={(event) => updateDraft({ board_title: event.target.value })}
                    disabled={isSaving}
                  />
                </label>

                <label className="auth-field portal-field-span-full">
                  <span>Bio</span>
                  <textarea
                    className="auth-input auth-input--textarea"
                    rows={8}
                    value={draftMember.bio}
                    onChange={(event) => updateDraft({ bio: event.target.value })}
                    disabled={isSaving}
                  />
                </label>

                <label className="auth-field">
                  <span>Photo alt text</span>
                  <input
                    className="auth-input"
                    type="text"
                    value={draftMember.photo_alt}
                    onChange={(event) => updateDraft({ photo_alt: event.target.value })}
                    disabled={isSaving}
                  />
                </label>

                {canManageAll ? (
                  <>
                    <label className="auth-field">
                      <span>Display order</span>
                      <input
                        className="auth-input"
                        type="number"
                        min={0}
                        value={draftMember.display_order}
                        onChange={(event) =>
                          updateDraft({
                            display_order: Number(event.target.value) || 0,
                          })
                        }
                        disabled={isSaving}
                      />
                    </label>

                    <label className="auth-field portal-field-span-full">
                      <span>Linked portal user ID</span>
                      <input
                        className="auth-input"
                        type="text"
                        value={draftMember.user_id ?? ''}
                        onChange={(event) =>
                          updateDraft({
                            user_id: event.target.value.trim() || null,
                          })
                        }
                        disabled={isSaving}
                      />
                    </label>
                  </>
                ) : null}

                <label className="portal-checkbox-field portal-field-span-full">
                  <input
                    type="checkbox"
                    checked={draftMember.is_visible}
                    onChange={(event) => updateDraft({ is_visible: event.target.checked })}
                    disabled={isSaving}
                  />
                  <span>Show this profile on the public board-members page.</span>
                </label>

                {error ? <div className="auth-error portal-field-span-full">{error}</div> : null}
                {feedback ? <div className="auth-success portal-field-span-full">{feedback}</div> : null}

                <div className="portal-inline-actions portal-field-span-full">
                  <button
                    type="button"
                    className="button-primary tap-target"
                    onClick={() => void handleSave()}
                    disabled={isSaving || isUploadingPhoto}
                  >
                    {isSaving
                      ? (isLocalFallback ? 'Saving locally...' : 'Saving profile...')
                      : (isLocalFallback ? 'Save in this browser' : 'Save board profile')}
                  </button>
                  <Link to="/board-members" className="button-secondary tap-target">
                    {isLocalFallback ? 'View current public page' : 'View public page'}
                  </Link>
                  {canManageAll ? (
                    <button
                      type="button"
                      className="button-ghost tap-target portal-danger-button"
                      onClick={() => void handleDelete()}
                      disabled={isSaving || isUploadingPhoto}
                    >
                      Delete profile
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </motion.div>
  );
}
